import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Plus, Minus } from "lucide-react";
import { listEntries, listCollections, updateEntry, deleteEntry } from "../lib/api";

const money = (n) => n == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

export default function CollectionDetail() {
  const { id } = useParams();
  const [entries, setEntries] = useState([]);
  const [collection, setCollection] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [cs, es] = await Promise.all([listCollections(), listEntries({ collection_id: id })]);
      setCollection((cs.collections || []).find((c) => c.id === id) || null);
      setEntries(es.entries || []);
    } catch {
      toast.error("Couldn't load collection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const filtered = useMemo(() => {
    let out = entries;
    if (filter === "owned") out = out.filter((e) => e.is_owned);
    if (filter === "wishlist") out = out.filter((e) => e.is_wishlist);
    return out;
  }, [entries, filter]);

  const totalValue = filtered.reduce((s, e) => s + (e.price_mid || 0) * (e.quantity || 1), 0);

  const bumpQty = async (e, delta) => {
    const next = Math.max(0, (e.quantity || 1) + delta);
    if (next === 0) {
      if (!window.confirm(`Remove ${e.name} from this collection?`)) return;
      await deleteEntry(e.id);
      load();
      return;
    }
    await updateEntry(e.id, { quantity: next });
    load();
  };

  const remove = async (e) => {
    if (!window.confirm(`Remove ${e.name}?`)) return;
    await deleteEntry(e.id);
    toast.success("Removed.");
    load();
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 sm:py-10">
      <Link to="/collections" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white mb-4" data-testid="back-to-collections">
        <ArrowLeft className="w-3.5 h-3.5" /> Collections
      </Link>
      <h1 className="text-3xl sm:text-5xl font-light tracking-tighter text-white mb-1">{collection?.name || "Collection"}</h1>
      <p className="text-sm text-white/55 mb-6">
        {filtered.length} cards · Total {money(totalValue)}
      </p>

      <div className="flex gap-2 mb-6">
        {["all", "owned", "wishlist"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-3 py-1.5 rounded-full text-xs tracking-wider uppercase transition ${
              filter === f
                ? "bg-amber-400/15 text-amber-200 border border-amber-300/40"
                : "bg-white/5 text-white/55 border border-transparent hover:bg-white/10"
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="animate-pulse space-y-2">
          {[...Array(5)].map((_, i) => <div key={i} className="h-20 bg-white/5 rounded-2xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass rounded-2xl p-10 text-center text-white/55">
          No cards yet. Scan a card and tap <span className="text-amber-200">Add to collection</span>.
        </div>
      ) : (
        <ul className="glass rounded-2xl divide-y divide-white/5">
          {filtered.map((e) => (
            <li key={e.id} className="flex items-center gap-3 p-3" data-testid={`entry-${e.id}`}>
              <img
                src={e.thumbnail_url || "https://placehold.co/40x56/1a1a1f/ffffff?text=?"}
                alt=""
                className="w-12 h-16 rounded object-cover border border-white/10 flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{e.name || "—"}</div>
                <div className="text-xs text-white/50 truncate">
                  {[e.set_code, e.card_number, e.variant !== "Normal" && e.variant, e.language].filter(Boolean).join(" · ")}
                </div>
                <div className="text-xs text-amber-200/80 font-mono mt-0.5">{money(e.price_mid)} each</div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => bumpQty(e, -1)} aria-label="decrement" data-testid={`dec-${e.id}`} className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center">
                  <Minus className="w-3 h-3" />
                </button>
                <span className="text-sm text-white w-6 text-center font-mono" data-testid={`qty-${e.id}`}>{e.quantity}</span>
                <button onClick={() => bumpQty(e, +1)} aria-label="increment" data-testid={`inc-${e.id}`} className="w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 text-white/70 flex items-center justify-center">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
              <button onClick={() => remove(e)} aria-label="delete" data-testid={`del-${e.id}`} className="p-1.5 text-white/30 hover:text-red-400 transition">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
